import React, { useCallback, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Loader2, Upload, ImagePlus, X } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];

export interface UploadResult {
  url: string;
  storagePath: string;
}

interface Props {
  /** Folder prefix inside the family-media bucket, e.g. "graph-avatars" */
  folder: string;
  /** Optional userId to namespace files (avoids collisions, no PII in path) */
  userId?: string | null;
  /** Called once a file has been validated and uploaded successfully */
  onUploaded: (result: UploadResult) => void | Promise<void>;
  /** Currently displayed image (preview). Pass through after a successful upload. */
  currentUrl?: string | null;
  /** Show a small "Remove" button that calls onRemove */
  onRemove?: () => void;
  /** Visual style: 'avatar' = square 96px tile, 'wide' = full-width drop zone */
  variant?: 'avatar' | 'wide';
  /** Allow multiple files (only used in 'wide' variant for galleries) */
  multiple?: boolean;
  /** Optional helper text */
  hint?: string;
  disabled?: boolean;
}

/**
 * PhotoUpload — drag & drop OR file picker, uploads to the family-media bucket.
 *
 * Validates type (image/*) and size (<= 5 MB), shows progress, and returns a
 * public URL via onUploaded. Can be reused for the avatar field on a profile
 * or for multi-photo galleries.
 */
const PhotoUpload: React.FC<Props> = ({
  folder,
  userId,
  onUploaded,
  currentUrl,
  onRemove,
  variant = 'avatar',
  multiple = false,
  hint,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Smoothed progress: Supabase JS doesn't surface per-byte progress, so we
  // simulate a forward-only progress bar that completes when the upload resolves.
  const [progress, setProgress] = useState(0);

  const validateFile = (file: File): string | null => {
    if (!file.type || !file.type.startsWith('image/')) {
      return 'Only image files are allowed (JPG, PNG, WEBP, GIF).';
    }
    if (ACCEPTED.length && !ACCEPTED.includes(file.type) && !file.type.startsWith('image/')) {
      return 'Unsupported image format.';
    }
    if (file.size > MAX_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      return `File is ${mb} MB. Max size is 5 MB.`;
    }
    return null;
  };

  const uploadOne = async (file: File): Promise<UploadResult | null> => {
    const err = validateFile(file);
    if (err) {
      toast.error(err);
      return null;
    }
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
    const safeUser = userId ? userId.replace(/[^a-zA-Z0-9-]/g, '') : 'anon';
    const path = `${folder}/${safeUser}/${Date.now()}-${Math.random()
      .toString(36)
      .slice(2, 8)}.${ext}`;

    const { error: upErr } = await supabase.storage
      .from('family-media')
      .upload(path, file, { cacheControl: '3600', upsert: false, contentType: file.type });
    if (upErr) {
      console.error('[PhotoUpload] upload error', upErr);
      toast.error(upErr.message || 'Upload failed');
      return null;
    }
    const { data } = supabase.storage.from('family-media').getPublicUrl(path);
    return { url: data.publicUrl, storagePath: path };
  };

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      if (disabled) return;
      const list = Array.from(files);
      if (list.length === 0) return;
      const queue = multiple ? list : list.slice(0, 1);

      setUploading(true);
      setProgress(8);
      // Slow ramp while the actual XHR runs — gives the user feedback even
      // though the SDK doesn't emit progress events.
      const tick = window.setInterval(() => {
        setProgress((p) => (p < 88 ? p + Math.max(2, (90 - p) * 0.08) : p));
      }, 120);
      try {
        for (const file of queue) {
          const result = await uploadOne(file);
          if (result) {
            await onUploaded(result);
          }
        }
        setProgress(100);
      } finally {
        window.clearInterval(tick);
        // brief pause so the bar visibly fills before disappearing
        window.setTimeout(() => {
          setUploading(false);
          setProgress(0);
        }, 350);
      }
    },
    [disabled, multiple, onUploaded, userId, folder] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const openPicker = () => {
    if (disabled || uploading) return;
    inputRef.current?.click();
  };

  // Avatar variant — compact square tile, with current image preview if any
  if (variant === 'avatar') {
    return (
      <div className="space-y-2">
        <div
          onClick={openPicker}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`relative w-24 h-24 rounded-full border-2 border-dashed flex items-center justify-center cursor-pointer transition overflow-hidden ${
            dragOver
              ? 'border-fuchsia-500 bg-fuchsia-50'
              : 'border-fuchsia-300 bg-fuchsia-50/40 hover:bg-fuchsia-50'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          role="button"
          aria-label="Upload profile photo"
        >
          {currentUrl ? (
            <>
              <img
                src={currentUrl}
                alt="Profile"
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/30 flex items-center justify-center transition">
                <Upload className="w-5 h-5 text-white opacity-0 hover:opacity-100" />
              </div>
            </>
          ) : uploading ? (
            <div className="flex flex-col items-center gap-1">
              <Loader2 className="w-5 h-5 animate-spin text-fuchsia-600" />
              <span className="text-[10px] text-fuchsia-700 font-semibold">
                {Math.round(progress)}%
              </span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1 text-fuchsia-600">
              <ImagePlus className="w-6 h-6" />
              <span className="text-[10px] font-semibold">Photo</span>
            </div>
          )}
          {currentUrl && onRemove && !uploading && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-rose-500 hover:bg-rose-600 text-white flex items-center justify-center shadow"
              aria-label="Remove photo"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {uploading && currentUrl && (
          <div className="w-24 h-1 bg-fuchsia-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-fuchsia-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
        {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            if (e.target.files) handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    );
  }

  // Wide variant — full-width drop zone for galleries
  return (
    <div className="space-y-2">
      <div
        onClick={openPicker}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`rounded-xl border-2 border-dashed p-5 cursor-pointer transition flex flex-col items-center justify-center gap-2 text-center ${
          dragOver
            ? 'border-fuchsia-500 bg-fuchsia-50'
            : 'border-fuchsia-300 bg-fuchsia-50/40 hover:bg-fuchsia-50'
        } ${disabled || uploading ? 'opacity-70 cursor-not-allowed' : ''}`}
        role="button"
      >
        {uploading ? (
          <>
            <Loader2 className="w-6 h-6 animate-spin text-fuchsia-600" />
            <div className="w-full max-w-xs h-2 bg-fuchsia-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-fuchsia-500 to-pink-500 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-xs text-fuchsia-700 font-semibold">
              Uploading… {Math.round(progress)}%
            </span>
          </>
        ) : (
          <>
            <Upload className="w-6 h-6 text-fuchsia-600" />
            <div className="text-sm font-semibold text-slate-800">
              Drag & drop {multiple ? 'photos' : 'a photo'} here
            </div>
            <div className="text-xs text-slate-500">or tap to choose from your device</div>
            <div className="text-[10px] text-slate-400">JPG, PNG, WEBP, GIF · max 5 MB</div>
          </>
        )}
      </div>
      {hint && <p className="text-[10px] text-slate-500">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
};

export default PhotoUpload;
